import { useQuery, useMutation } from "@apollo/client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  CardElement,
  useElements,
  useStripe,
  Elements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { PaymentIntent, StripeElements } from "@stripe/stripe-js";
import axios from "axios";
import Link from "next/link";
import { useRouter } from "next/router";
import { VFC, useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import * as config from "../config";
import {
  CREATE_MEMBER_WITH_MONTHLY_PAYMENT,
  CREATE_MEMBER_WITH_ONE_TIME_PAYMENT,
  CREATE_MEMBER_WITH_BANK_PAYMENT,
  GET_MEMBER_BY_EMAIL,
  CREATE_EVENT,
} from "../queries/queries";
import {
  CreateMemberWithMonthlyPaymentMutation,
  CreateMemberWithOneTimePaymentMutation,
  CreateMemberWithBankPaymentMutation,
  GetMemberByEmailQuery,
  CreateEventMutation,
} from "../types/generated/graphql";
import { Member, Payment } from "../types/types";
import { fetchPostJSON } from "../utils/api-helpers";

import { Step1 } from "./entry/Step1";
import { Step2 } from "./entry/Step2";

import { Step4 } from "./entry/Step4";

import { StepProgressBar } from "./entry/StepProgressBar";
import CustomDonationInput from "./payment/CustomDonationInput";
import ElementsForm from "./payment/ElementsForm";

interface EntryProps {
  paymentIntent?: PaymentIntent | null;
}

export const Entry: VFC<EntryProps> = ({ paymentIntent = null }) => {
  const router = useRouter();
  const [step, setStep] = useState(1);
  const [user, setUser] = useState({
    company_flag: false,
    receipt: false,
  } as Member);
  const { refetch } = useQuery<GetMemberByEmailQuery>(GET_MEMBER_BY_EMAIL, {
    variables: { email_address: user.email_address },
  });
  const [insert_member_with_monthly] =
    useMutation<CreateMemberWithMonthlyPaymentMutation>(
      CREATE_MEMBER_WITH_MONTHLY_PAYMENT
    );
  const [insert_member_with_one_time] =
    useMutation<CreateMemberWithOneTimePaymentMutation>(
      CREATE_MEMBER_WITH_ONE_TIME_PAYMENT
    );
  const [insert_member_with_bank] =
    useMutation<CreateMemberWithBankPaymentMutation>(
      CREATE_MEMBER_WITH_BANK_PAYMENT
    );
  const [create_event] = useMutation<CreateEventMutation>(CREATE_EVENT);

  const [payment, setPayment] = useState({
    status: "initial",
    isPeriodic: false,
    method: "credit",
    transfer_fee_flag: false,
    amount: 1000,
    cardholderName: "",
  } as Payment);
  const stripe = useStripe();

  const [errorMessage, setErrorMessage] = useState("");
  const [paymentType, setPaymentType] = useState("");

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors, isSubmitting },
    getValues,
  } = useForm({
    criteriaMode: "all",
  });

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setPayment({
      ...payment,
      [e.currentTarget.name]: e.currentTarget.value,
    });
  };

  const handleSelectTransferFeeFlag: React.ChangeEventHandler<
    HTMLInputElement
  > = (e) => {
    setPayment({
      ...payment,
      transfer_fee_flag: e.currentTarget.checked,
    });
  };

  const showStepForm = () => {
    switch (step) {
      case 1:
        return (
          <Step1
            step={step}
            user={user}
            setUser={setUser}
            payment={payment}
            setPayment={setPayment}
          />
        );
      case 2:
        return (
          <Step2 step={step} register={register} user={user} errors={errors} />
        );
      case 3:
        return step3();
      case 4:
        return <Step4 step={step} user={user} payment={payment} />;
    }
  };

  const selectPaymentMethod = (selectedValue: string) => {
    if (payment["isPeriodic"] && selectedValue === "bank") {
      toast("銀行振込を選択できるのは「1回のみ寄付」の場合のみです。");
    } else {
      setPayment({ ...payment, method: selectedValue });
    }
  };

  const step3 = () => {
    return (
      <>
        <div className="l-container">
          <StepProgressBar step={3} />

          <div className="p-pageTtl">
            <div className="p-pageTtl__inner">
              <h2 className="p-pageTtl__ttl">支払い方法</h2>
            </div>
          </div>

          <div className="p-tabs -cols2">
            <div className="p-tabs__inner">
              <ul>
                <li className={payment["method"] === "credit" ? "is-cr" : ""}>
                  <a
                    onClick={() => {
                      selectPaymentMethod("credit");
                    }}
                  >
                    <span>クレジットカードでお支払い</span>
                  </a>
                </li>
                <li className={payment["method"] === "bank" ? "is-cr" : ""}>
                  <a
                    onClick={() => {
                      selectPaymentMethod("bank");
                    }}
                  >
                    <span>銀行振込</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="l-container">
          <div className="p-cardForm">
            <div className="l-container -sm">
              {payment.method === "credit" ? (
                <>
                  <div className="p-form">
                    <ElementsForm
                      paymentIntent={paymentIntent}
                      elements={elements}
                      payment={payment}
                      setPayment={setPayment}
                    />
                  </div>

                  <div className="p-cardForm__info">
                    <p className="p-cardForm__infoTtl">
                      ご利用になれるクレジットカード
                    </p>
                    <p className="p-cardForm__infoCard">
                      <img
                        src="/assets/img/common/icon-cards.png"
                        alt="ご利用になれるクレジットカード"
                      />
                    </p>
                    <div className="p-cardForm__infoTxt">
                      <p>
                        クレジットカード情報は弊社で保存・管理は行っておらず、決済代行サービス（Stripe）にて
                        <br />
                        PCI-DSSと呼ばれるカード情報セキュリティの国際統一基準に準拠して、
                        <br />
                        暗号化した情報で管理していますのでご安心ください。
                      </p>
                    </div>
                  </div>
                  <CustomDonationInput
                    className="elements-style"
                    name="amount"
                    amount={payment.amount}
                    min={config.MIN_AMOUNT}
                    max={config.MAX_AMOUNT}
                    onChange={handleInputChange}
                    selectTransferFeeFlag={handleSelectTransferFeeFlag}
                    payment={payment}
                  />
                </>
              ) : (
                <>
                  <div className="p-cardForm__bankMsg">
                    <p>
                      お振込先指定口座など、別途メールを送らせていただきます。
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </>
    );
  };

  const onSubmit = async (data: any) => {
    setUser({
      ...user,
      ...data,
    });

    return new Promise((resolve) => {
      return nextStep(resolve, data);
    });
  };

  const nextStep = async (resolve: any, data: any) => {
    handleSubmit(onSubmit);
    if (step === 4) {
      await handlePayment();
    } else if (step === 2) {
      const res = await checkExitMember(data);
      if (res.data.members.length === 0) {
        setStep(3);
        window.scrollTo({
          top: 0,
        });
      } else {
        toast.error("すでにこのメールアドレスは会員登録されています。");
      }
    } else if (step === 3) {
      setStep(4);
      window.scrollTo({
        top: 0,
      });
    } else {
      setStep(step + 1);
      window.scrollTo({
        top: 0,
      });
    }
    return resolve(1);
  };

  const previousStep = () => {
    setStep(step - 1);
    window.scrollTo({
      top: 0,
    });
    return;
  };

  const elements = useElements();

  const checkExitMember = async (data: any) => {
    return await refetch({ email_address: data.email_address });
  };

  const handlePayment = async () => {
    // Abort if form isn't valid
    // if (!e.currentTarget.reportValidity()) return;

    if (!elements) return;

    setPayment({ ...payment, status: "processing" });

    const customer_response = await fetchPostJSON("/api/stripe_customers", {
      name: user.name,
      email: user.email_address,
    });

    if (customer_response.statusCode === 500) {
      setPayment({ ...payment, status: "error" });
      setErrorMessage(customer_response.message);
      console.log(customer_response.message);
      setStep(3);
      window.scrollTo({
        top: 0,
      });
      toast.error("支払い処理でエラーが発生しました。");
      return;
    }

    if (payment.method === "bank") {
      try {
        const { data } = await axios.post(
          `${process.env.NEXT_PUBLIC_AUTH0_SIGNUP_URL}`,
          {
            client_id: process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID,
            email: user.email_address,
            password: user.password,
            connection: process.env.NEXT_PUBLIC_AUTH0_CONNECTION,
          },
          { headers: { "Content-Type": "application/json" } }
        );
        await insert_member_with_bank({
          variables: {
            ...user,
            auth0_id: `auth0|${data._id}`,
            stripe_customer_id: customer_response.id,
          },
        });
        // await insert_donations_one({
        //   variables: {
        //     amount: 0,
        //     donation_type: "one_time",
        //     email_address: user.email_address,
        //     payment_method: "bank",
        //     transfer_fee_flag: false,
        //     transfer_fee: 0,
        //     member_auth0_id: `auth0|${data._id}`,
        //   },
        // });
        const event_res = await create_event({
          variables: {
            content: `${user.name}(auth0|${data._id})さんが会員登録し、振り込みでの寄付を希望しました`,
          },
        });
        router.replace("/thanks");
      } catch (err) {
        if (
          axios.isAxiosError(err) &&
          err.response &&
          err.response.status === 400
        ) {
          console.log(err.response);
          setStep(3);
          window.scrollTo({
            top: 0,
          });
        } else {
          console.log(err);
          setStep(3);
          window.scrollTo({
            top: 0,
          });
        }
        return;
      }
    } else {
      const amount = payment.transfer_fee_flag
        ? Math.ceil(payment.amount / 0.964)
        : payment.amount;

      if (amount < config.MIN_AMOUNT) {
        setStep(3);
        window.scrollTo({
          top: 0,
        });
        toast.error("支払いの最低額は1,000円以上です。");
        return;
      }
      // Stripを利用した決済
      if (payment.isPeriodic) {
        // 定期支払

        const response = await fetchPostJSON("/api/payment_intents", {
          amount: amount,
          payment_intent_id: paymentIntent?.id,
          customer_id: customer_response.id,
        });

        if (response.statusCode === 500) {
          setPayment({ ...payment, status: "error" });
          console.log(response.message);
          setStep(3);
          window.scrollTo({
            top: 0,
          });
          toast.error("支払い処理でエラーが発生しました。");
          return;
        }

        const { error, paymentIntent: resutlPaymentintent } =
          await stripe!.confirmPayment({
            elements,
            confirmParams: {
              payment_method_data: {
                billing_details: {
                  name: payment.cardholderName,
                },
              },
            },
            redirect: "if_required",
          });

        if (error) {
          setPayment({ ...payment, status: "error" });
          console.log(error.message);
          setStep(3);
          window.scrollTo({
            top: 0,
          });
          toast.error("支払い処理でエラーが発生しました。");
          return;
        }

        const subscription_response = await fetchPostJSON(
          "/api/stripe/subscription",
          {
            amount: amount,
            customer_id: customer_response.id,
            payment_method_id: resutlPaymentintent!.payment_method,
          }
        );

        if (subscription_response.statusCode === 500) {
          setPayment({ ...payment, status: "error" });
          setErrorMessage(response.message);
          console.log(response.message);
          setStep(3);
          window.scrollTo({
            top: 0,
          });
          toast.error("定期支払い処理でエラーが発生しました。");
          return;
        }

        if (paymentIntent) {
          try {
            const { data } = await axios.post(
              `${process.env.NEXT_PUBLIC_AUTH0_SIGNUP_URL}`,
              {
                client_id: process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID,
                email: user.email_address,
                password: user.password,
                connection: process.env.NEXT_PUBLIC_AUTH0_CONNECTION,
              },
              { headers: { "Content-Type": "application/json" } }
            );
            await insert_member_with_monthly({
              variables: {
                ...user,
                auth0_id: `auth0|${data._id}`,
                stripe_customer_id: customer_response.id,
                stripe_subscription_id: subscription_response.id,
                stripe_payment_id: resutlPaymentintent!.id,
                amount: amount,
                transfer_fee_flag: payment.transfer_fee_flag,
                transfer_fee: Math.round(amount * 0.036),
              },
            });
            const event_res = await create_event({
              variables: {
                content: `${user.name}(auth0|${data._id})さんが会員登録し、マンスリーの${amount}円の寄付を開始しました`,
              },
            });
            router.replace("/thanks");
            return;
          } catch (err) {
            if (
              axios.isAxiosError(err) &&
              err.response &&
              err.response.status === 400
            ) {
              console.log(err.response);
              setStep(3);
              window.scrollTo({
                top: 0,
              });
            } else {
              console.log(err);
              setStep(3);
              window.scrollTo({
                top: 0,
              });
            }
            return;
          }
        }
      } else {
        // 一回限りの決済

        // Create a PaymentIntent with the specified amount.
        const response = await fetchPostJSON("/api/payment_intents", {
          amount: amount,
          payment_intent_id: paymentIntent?.id,
          customer_id: customer_response.id,
        });
        // setPayment(response);

        if (response.statusCode === 500) {
          setPayment({ ...payment, status: "error" });
          setErrorMessage(response.message);
          console.log(response.message);
          setStep(3);
          window.scrollTo({
            top: 0,
          });
          toast.error("支払い処理でエラーが発生しました。");
          return;
        }

        // Use your card Element with other Stripe.js APIs
        const { error, paymentIntent: resutlPaymentintent } =
          await stripe!.confirmPayment({
            elements,
            confirmParams: {
              payment_method_data: {
                billing_details: {
                  name: payment.cardholderName,
                },
              },
            },
            redirect: "if_required",
          });

        if (error) {
          setPayment({ ...payment, status: "error" });
          setErrorMessage(error.message ?? "An unknown error occurred");
          console.log(error.message);
          setStep(3);
          window.scrollTo({
            top: 0,
          });
          toast.error("支払い処理でエラーが発生しました。");
          return;
        }

        const update_response = await fetchPostJSON(
          "/api/stripe/update_customer_default_payment_method",
          {
            customer_id: customer_response.id,
            payment_method_id: resutlPaymentintent!.payment_method,
          }
        );

        if (update_response.statusCode === 500) {
          setPayment({ ...payment, status: "error" });
          setErrorMessage(response.message);
          console.log(response.message);
          setStep(3);
          window.scrollTo({
            top: 0,
          });
          toast.error("エラーが発生しました。");
          return;
        }

        if (paymentIntent) {
          try {
            const { data } = await axios.post(
              `${process.env.NEXT_PUBLIC_AUTH0_SIGNUP_URL}`,
              {
                client_id: process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID,
                email: user.email_address,
                password: user.password,
                connection: process.env.NEXT_PUBLIC_AUTH0_CONNECTION,
              },
              { headers: { "Content-Type": "application/json" } }
            );
            const member = await insert_member_with_one_time({
              variables: {
                ...user,
                auth0_id: `auth0|${data._id}`,
                stripe_customer_id: customer_response.id,
                stripe_payment_id: resutlPaymentintent!.id,
                amount: amount,
                transfer_fee_flag: payment.transfer_fee_flag,
                transfer_fee: Math.round(amount * 0.036),
              },
            });
            const event_res = await create_event({
              variables: {
                content: `${user.name}(auth0|${data._id})さんが会員登録し、${amount}円の寄付をしました`,
              },
            });
            router.replace("/thanks");
          } catch (err) {
            if (
              axios.isAxiosError(err) &&
              err.response &&
              err.response.status === 400
            ) {
              console.log(err.response);
              setStep(3);
              window.scrollTo({
                top: 0,
              });
            } else {
              console.log(err);
              setStep(3);
              window.scrollTo({
                top: 0,
              });
            }
            return;
          }
        }
      }
    }
  };

  return (
    <>
      <div className="entry-page">
        {step == 1 && (
          <section className="login-syn -top">
            <div className="l-container -sm">
              <a
                className="u-over login-syn__inner"
                href="https://syncable.biz/associate/miryokukapf/donate?amount=2000&frequency=monthly&recurring=true"
                target="_blank"
                rel="noreferrer"
              >
                <div className="login-syn__txt">
                  <p>
                    2022年9月1日より以前にマンスリー会員になった方は、こちらからログインをお願いいたします。または、ログイン情報を登録されていない方は
                    Sycnableからのメールをご確認ください。
                  </p>
                </div>
                <p className="login-syn__logo">
                  <span>
                    <img src="/assets/img/common/logo-syn.svg" alt="Syncable" />
                  </span>
                </p>
              </a>
            </div>
          </section>
        )}

        <section className="entry-form">
          {step != 3 ? (
            <>
              <div className="entry-form__inner">{showStepForm()}</div>
            </>
          ) : (
            <></>
          )}

          <div hidden={step != 3}>
            <div className="entry-form__inner">{step3()}</div>
          </div>
        </section>

        <section className="entry-btn">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="entry-btn__inner">
              <div className="l-container">
                <div className="p-pageNav">
                  <div className="p-pageNav__inner">
                    <div className="p-pageNav__btns">
                      <div className="p-pageNav__btn">
                        <p className="c-btn -wt -prev">
                          {step > 1 && (
                            <a onClick={previousStep} className="u-over">
                              戻る
                            </a>
                          )}
                        </p>
                      </div>
                      <div className="p-pageNav__btn">
                        <p className="c-btn">
                          <button
                            disabled={isSubmitting}
                            type="submit"
                            className="u-over"
                          >
                            {isSubmitting && (
                              <FontAwesomeIcon icon="spinner" spin />
                            )}
                            次へ
                          </button>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </section>
      </div>
    </>
  );
};
